import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, X, UserPlus, Mail } from 'lucide-react'

import { listTenantMailboxes, listTenants, provisionUser } from '../lib/api'

export default function AdminTenant() {
  const qc = useQueryClient()
  const { slug = '' } = useParams()
  const [showForm, setShowForm] = useState(false)
  const [envFilter, setEnvFilter] = useState<'all' | 'prod' | 'preprod'>('all')

  const tenants = useQuery({ queryKey: ['tenants'], queryFn: listTenants })
  const tenant = tenants.data?.tenants.find(t => t.slug === slug)

  const mailboxes = useQuery({
    queryKey: ['mailboxes', slug, envFilter],
    queryFn: () => listTenantMailboxes(slug, envFilter === 'all' ? undefined : envFilter),
  })

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line px-6 py-5 flex items-start gap-4">
        <Link to="/admin" className="pill-ghost mt-1">
          <ArrowLeft size={16} /> Tenants
        </Link>
        <div className="flex-1">
          <div className="h-eyebrow">Tenant</div>
          <h1 className="h-section">{tenant?.name || slug}</h1>
          <div className="text-sm text-ink-500 mt-1 font-mono">
            {slug === 'plustag' ? 'plustag.email · preprod.plustag.email' :
              `${slug}.plustag.email · ${slug}.preprod.plustag.email`}
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="pill-primary">
          <UserPlus size={16} /> Add user
        </button>
      </header>

      <div className="px-6 py-3 border-b border-line flex items-center gap-2 text-sm">
        <span className="h-eyebrow mr-2">Env</span>
        {(['all', 'prod', 'preprod'] as const).map(e => (
          <button key={e}
                  onClick={() => setEnvFilter(e)}
                  className={`pill ${envFilter === e ? 'bg-accent text-raised' : 'text-ink-700 hover:bg-canvas'}`}>
            {e}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {mailboxes.isLoading && <div className="text-ink-500">Loading…</div>}
        <ul className="space-y-2">
          {mailboxes.data?.mailboxes.map(m => (
            <li key={m.id} className="card p-3 flex items-center gap-3">
              <Mail size={16} className="text-ink-500" />
              <div className="flex-1 font-mono text-sm text-ink-900">{m.address}</div>
              <span className={`pill text-[10px] ${m.env === 'prod' ? 'bg-accent text-raised' : 'bg-canvas text-ink-500'}`}>
                {m.env}
              </span>
              {!m.owner_user_id && <span className="text-xs text-ink-500">system</span>}
            </li>
          ))}
          {mailboxes.data && mailboxes.data.mailboxes.length === 0 && (
            <li className="card-inset p-6 text-ink-500 text-sm">No mailboxes yet on this tenant / env.</li>
          )}
        </ul>
      </div>

      {showForm && (
        <ProvisionModal slug={slug} onClose={() => setShowForm(false)}
                       onDone={() => {
                         qc.invalidateQueries({ queryKey: ['mailboxes', slug] })
                         setShowForm(false)
                       }} />
      )}
    </div>
  )
}

function ProvisionModal({ slug, onClose, onDone }: {
  slug: string; onClose: () => void; onDone: () => void
}) {
  const [handle, setHandle] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [envs, setEnvs] = useState<Array<'prod' | 'preprod'>>(['prod', 'preprod'])

  const provision = useMutation({
    mutationFn: () => provisionUser(slug, {
      handle: handle.trim().toLowerCase(),
      contact_email: contactEmail.trim(),
      display_name: displayName.trim() || undefined,
      envs,
    }),
    onSuccess: onDone,
  })

  return (
    <div className="fixed inset-0 bg-ink-900/40 grid place-items-center z-50 p-6">
      <div className="card w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="h-eyebrow">Provision</div>
            <h2 className="text-lg font-semibold text-ink-900">New user on {slug}</h2>
          </div>
          <button onClick={onClose} className="pill-ghost"><X size={16} /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <div className="h-eyebrow mb-1">Handle</div>
            <input value={handle} onChange={e => setHandle(e.target.value)}
                   placeholder="alice" className="field-flush" autoFocus />
            <div className="text-xs text-ink-500 mt-1">
              Address: <span className="font-mono text-ink-700">
                {slug === 'plustag'
                  ? `${handle || '{handle}'}@plustag.email`
                  : `${handle || '{handle}'}@${slug}.plustag.email`}
              </span>
            </div>
          </label>

          <label className="block">
            <div className="h-eyebrow mb-1">Contact email (invite goes here)</div>
            <input value={contactEmail} onChange={e => setContactEmail(e.target.value)}
                   type="email" placeholder="alice@example.com" className="field-flush" />
          </label>

          <label className="block">
            <div className="h-eyebrow mb-1">Display name (optional)</div>
            <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                   placeholder="Alice Example" className="field-flush" />
          </label>

          <div>
            <div className="h-eyebrow mb-1">Envs</div>
            <div className="flex gap-2">
              {(['prod', 'preprod'] as const).map(e => (
                <label key={e} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={envs.includes(e)}
                         onChange={ev => setEnvs(ev.target.checked
                           ? [...envs, e] : envs.filter(x => x !== e))} />
                  {e}
                </label>
              ))}
            </div>
          </div>

          {provision.isError && (
            <div className="text-sm text-red-700 bg-red-50 p-3 rounded-soft">
              {provision.error instanceof Error ? provision.error.message : String(provision.error)}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="pill-ghost">Cancel</button>
            <button onClick={() => provision.mutate()}
                    disabled={provision.isPending || !handle || !contactEmail || envs.length === 0}
                    className="pill-primary disabled:opacity-50">
              <Check size={16} />
              {provision.isPending ? 'Provisioning…' : 'Provision'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
